import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Tabs, Tab, Button, TextField, Grid,
  IconButton, Tooltip, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip
} from "@mui/material";
import {
  Add as AddIcon, Delete as DeleteIcon, FilterList as FilterIcon,
  FileDownload as ExportIcon, Login as CheckInIcon, Logout as CheckOutIcon,
  HistoryOutlined as HistoryOutlinedIcon, Close as CloseIcon
} from "@mui/icons-material";
import { fetchData, postData, deleteData } from "../../BaseUrl";
import Dynamictablepagination from "../../DynamicComponent/Dynamictablepagination";
import Toast from "../../DynamicComponent/Toast";
import AddInventoryDialog from "./AddInventoryDialog";
import CheckOutReasonDialog from "./CheckOutReasonDialog";


const AdminInventory = () => {
  const [tabValue, setTabValue] = useState(0);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [filters, setFilters] = useState({ search: "", category: "" });
  const [selectedItems, setSelectedItems] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [checkOutItem, setCheckOutItem] = useState(null);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadItems = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (filters.search) params.append("search", filters.search);
      if (filters.category) params.append("category", filters.category);

      const result = await fetchData(`admin/inventory/?${params.toString()}`);
      if (result?.data) {
        setItems(result.data.items || result.data || []);
        setPagination(result.data.pagination || { total: 0, page: 1, totalPages: 1 });
      }
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to load inventory" });
    }
  }, [filters]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleCheckIn = async (item) => {
    try {
      await postData(`admin/inventory/${item.id}/check-in`, {});
      setToast({ open: true, severity: "success", message: `${item.itemName || item.name} checked in` });
      loadItems(pagination.page);
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Check-in failed" });
    }
  };

  const handleCheckOutSuccess = (message) => {
    setToast({ open: true, severity: "success", message });
    setCheckOutOpen(false);
    setCheckOutItem(null);
    loadItems(pagination.page);
  };

  const handleDelete = async () => {
    if (selectedItems.length === 0) return;
    try {
      for (const id of selectedItems) {
        await deleteData(`admin/inventory/${id}`);
      }
      setToast({ open: true, severity: "success", message: `${selectedItems.length} item(s) deleted` });
      setSelectedItems([]);
      loadItems();
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Delete failed" });
    }
  };

  const handleAddSuccess = (message) => {
    setToast({ open: true, severity: "success", message });
    setAddDialogOpen(false);
    loadItems();
  };

  const handleExport = () => {
    setToast({ open: true, severity: "info", message: "Export started..." });
  };

  const fetchHistory = async (itemId) => {
    try {
      const res = await fetchData(`admin/inventory/${itemId}/logs`);
      setHistoryData(res?.data || res || []);
    } catch {
      setHistoryData([]);
    }
  };

  const handleOpenHistory = async () => {
    try {
      const result = await fetchData("admin/inventory/history");
      setHistoryData(result?.data || []);
    } catch (err) {
      setHistoryData([]);
    }
    setHistoryDialogOpen(true);
  };

  const columns = [
    { label: "Item ID", field: "itemId" },
    { label: "Item Name", field: "itemName" },
    { label: "Category", field: "category" },
    { label: "Quantity", field: "quantity" },
    { label: "Assigned To", field: "assignedTo" },
    { label: "Reason", field: "reason" },
    { label: "Location", field: "location" },
    { label: "Last Updated", field: "lastUpdated" },
    { label: "Action", field: "actions" }
  ];

  const rows = items.map((item) => ({
    ...item,
    itemId: item.itemId || item.id || "-",
    itemName: item.itemName || item.name || "-",
    category: item.category || "-",
    quantity: item.quantity || 0,
    assignedTo: item.assignedTo || "-",
    reason: item.reason || "-",
    location: item.location || "-",
    lastUpdated: item.lastUpdated || item.updatedAt?.slice(0, 10) || "-",
    actions: (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Button size="small" variant="contained" startIcon={<CheckInIcon />}
          onClick={() => handleCheckIn(item)}
          sx={{ backgroundColor: "#2196f3", textTransform: "none", fontSize: 12,
            "&:hover": { backgroundColor: "#1976d2" } }}>
          Check In
        </Button>
        <Button size="small" variant="contained" startIcon={<CheckOutIcon />}
          onClick={() => { setCheckOutItem(item); setCheckOutOpen(true); }}
          sx={{ backgroundColor: "#f44336", textTransform: "none", fontSize: 12,
            "&:hover": { backgroundColor: "#d32f2f" } }}>
          Check Out
        </Button>
        <Tooltip title="View Item History">
          <IconButton size="small"
            onClick={async () => { await fetchHistory(item.id || item._id); setHistoryDialogOpen(true); }}
            sx={{ color: "#244C77", border: "1px solid #e0e0e0", borderRadius: 1 }}>
            <HistoryOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    )
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={1}>Pages / Inventory Management</Typography>
      <Typography variant="h5" fontWeight="bold" mb={3}>Inventory Management</Typography>

      {/* Tab Toggle */}
      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tab label="Inventory" />
        <Tab label="Check-In/Out" />
      </Tabs>

      {/* Top Actions */}
      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField size="small" fullWidth placeholder="Search items..."
            value={filters.search} onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && loadItems()} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select value={filters.category} label="Category"
              onChange={(e) => setFilters(p => ({ ...p, category: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Stationery">Stationery</MenuItem>
              <MenuItem value="Supplies">Supplies</MenuItem>
              <MenuItem value="Electronics">Electronics</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <Button variant="outlined" startIcon={<FilterIcon />} onClick={() => loadItems()}
            sx={{ height: 40 }}>Apply</Button>
        </Grid>
        <Grid item xs={12} sm={5} sx={{ display: "flex", justifyContent: "flex-end", gap: 1, alignItems: "center" }}>
          {selectedItems.length > 0 && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}
              sx={{ textTransform: "none" }}>
              Delete ({selectedItems.length})
            </Button>
          )}
          <Tooltip title="Check-In/Out History">
            <IconButton onClick={handleOpenHistory}
              sx={{ border: "1px solid #e0e0e0", borderRadius: 1, color: "#244C77" }}>
              <HistoryOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExport}
            sx={{ textTransform: "none" }}>Export</Button>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{ background: "linear-gradient(90deg, #4A7BA8, #244C77)", textTransform: "none" }}>
            Add Inventory
          </Button>
        </Grid>
      </Grid>

      <Dynamictablepagination columns={columns} rows={rows} page={pagination?.page || 1} totalPages={pagination?.totalPages || 1} handleChangePage={(e, newPage) => loadItems(newPage)} />

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight="bold">Check-In / Check-Out History</Typography>
          <IconButton onClick={() => setHistoryDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  {["Action", "Item", "Date", "User"].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {historyData.map((h, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <Chip label={h.action} size="small"
                        sx={{
                          backgroundColor: h.action === "Check In" ? "#e8f5e9" : "#fce4ec",
                          color: h.action === "Check In" ? "#4caf50" : "#f44336",
                          fontWeight: 600
                        }} />
                    </TableCell>
                    <TableCell>{h.item}</TableCell>
                    <TableCell>{h.date}</TableCell>
                    <TableCell>{h.user}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)} variant="outlined"
            sx={{ textTransform: "none" }}>Close</Button>
        </DialogActions>
      </Dialog>

      <AddInventoryDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}
        onSuccess={handleAddSuccess} />

      {checkOutItem && (
        <CheckOutReasonDialog open={checkOutOpen} onClose={() => { setCheckOutOpen(false); setCheckOutItem(null); }}
          item={checkOutItem} onSuccess={handleCheckOutSuccess} />
      )}

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminInventory;
